import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PawPrint, Loader2, Eye, EyeOff, MapPin, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [locationStatus, setLocationStatus] = useState('checking'); // checking, granted, denied, unavailable
  const [locationError, setLocationError] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  // Check location permission on mount
  useEffect(() => {
    checkLocationPermission();
  }, []);

  const checkLocationPermission = async () => {
    if (!navigator.geolocation) {
      setLocationStatus('unavailable');
      setLocationError('Geolocation is not supported by your browser');
      return;
    }

    try {
      // Check permission state
      if (navigator.permissions) {
        const result = await navigator.permissions.query({ name: 'geolocation' });
        if (result.state === 'denied') {
          setLocationStatus('denied');
          setLocationError('Location access has been denied. Please enable it in your browser settings.');
          return;
        }
      }

      // Try to get position to trigger permission prompt
      navigator.geolocation.getCurrentPosition(
        () => {
          setLocationStatus('granted');
          setLocationError('');
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            setLocationStatus('denied');
            setLocationError('Location access was denied. Please enable location in your browser settings and refresh the page.');
          } else {
            // Other errors (position unavailable, timeout) - still allow login
            setLocationStatus('granted');
          }
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    } catch (error) {
      setLocationStatus('granted'); // Allow login on error
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Note: Location check is primarily for employees. 
    // We allow login to proceed and check location on clock-in instead.
    // This prevents blocking admins who may not need location.
    
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.full_name}!`);
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        // For employees, warn if location is denied but allow login
        if (locationStatus === 'denied') {
          toast.warning('Location is required for clock-in. Please enable location access.');
        }
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid email or password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden">
        <img 
          src="https://images.unsplash.com/photo-1759419281924-0637211e60fd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwxfHxjYWxtJTIwZm9yZXN0JTIwb2ZmaWNlJTIwd29ya3NwYWNlfGVufDB8fHx8MTc3MDQxMTAxMHww&ixlib=rb-4.1.0&q=85"
          alt="Forest workspace"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-950/90 via-emerald-900/80 to-emerald-950/90" />
        <div className="relative z-10 flex flex-col justify-center p-16">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-2xl">
              <PawPrint className="w-10 h-10 text-white" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white font-heading">Lil Bear Tracks</h1>
              <p className="text-emerald-300 text-lg">Time Tracking Made Simple</p>
            </div>
          </div>
          <div className="space-y-6 max-w-md">
            <p className="text-emerald-100 text-xl leading-relaxed">
              Track your work hours effortlessly. Clock in, take breaks, and stay on top of your time.
            </p>
            <div className="flex gap-4 pt-4">
              <div className="bg-emerald-800/50 rounded-2xl p-4 backdrop-blur-sm border border-emerald-700/30">
                <p className="text-3xl font-bold text-white">Easy</p>
                <p className="text-emerald-300 text-sm">One-tap clock in</p>
              </div>
              <div className="bg-emerald-800/50 rounded-2xl p-4 backdrop-blur-sm border border-emerald-700/30">
                <p className="text-3xl font-bold text-white">Fast</p>
                <p className="text-emerald-300 text-sm">Real-time tracking</p>
              </div>
              <div className="bg-emerald-800/50 rounded-2xl p-4 backdrop-blur-sm border border-emerald-700/30">
                <p className="text-3xl font-bold text-white">Clear</p>
                <p className="text-emerald-300 text-sm">Detailed reports</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Right side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-stone-50">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-10">
            <div className="w-14 h-14 bg-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
              <PawPrint className="w-8 h-8 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-stone-900 font-heading">Lil Bear Tracks</h1>
              <p className="text-stone-500">Time Tracking Made Simple</p>
            </div>
          </div>

          <Card className="border-0 shadow-2xl shadow-stone-200/50 bg-white/80 backdrop-blur-xl">
            <CardHeader className="space-y-2 pb-6">
              <CardTitle className="text-2xl font-heading">Welcome back</CardTitle>
              <CardDescription className="text-base">
                Sign in to your account to continue
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Location Status Indicator */}
              {locationStatus === 'checking' && (
                <div className="mb-6 p-4 bg-blue-50 rounded-xl border border-blue-200 flex items-center gap-3">
                  <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
                  <span className="text-blue-700 text-sm">Checking location permissions...</span>
                </div>
              )}
              
              {locationStatus === 'denied' && (
                <div className="mb-6 p-4 bg-rose-50 rounded-xl border border-rose-200">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-rose-600 mt-0.5" />
                    <div>
                      <p className="text-rose-700 font-medium text-sm">Location Access Required</p>
                      <p className="text-rose-600 text-sm mt-1">{locationError}</p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="mt-3 text-rose-600 border-rose-300 hover:bg-rose-100"
                        onClick={() => window.location.reload()}
                      >
                        Refresh Page
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {locationStatus === 'granted' && (
                <div className="mb-6 p-3 bg-emerald-50 rounded-xl border border-emerald-200 flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-emerald-600" />
                  <span className="text-emerald-700 text-sm">Location access enabled</span>
                </div>
              )}

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-stone-700">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                    className="h-12 bg-stone-50 border-stone-200 focus:border-emerald-500 focus:ring-emerald-500/20"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-stone-700">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      data-testid="password-input"
                      className="h-12 bg-stone-50 border-stone-200 focus:border-emerald-500 focus:ring-emerald-500/20 pr-12"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-4 top-1/2 -translate-y-1/2 text-stone-400 hover:text-stone-600 transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <Button 
                  type="submit" 
                  className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold rounded-xl shadow-lg shadow-emerald-600/20 transition-all active:scale-[0.98]"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Signing in...
                    </>
                  ) : (
                    'Sign In'
                  )}
                </Button>
              </form>
              <div className="mt-8 pt-6 border-t border-stone-100">
                <p className="text-sm text-center text-stone-500">
                  Contact your administrator for account access
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
